<script setup lang="ts">
import { clearError, navigateTo, type NuxtError } from "#app";
import { computed, onMounted } from "vue";
import { useAuthStore } from "@/stores/auth";
import { clearClientAuthStorage } from "@/utils/authState";

const props = defineProps({
  error: Object as () => NuxtError,
});

const handleError = () => {
  clearError({ redirect: "/" });
};

// 401：清登录态并跳登录页
onMounted(() => {
  if (props.error?.statusCode === 401) {
    try {
      useAuthStore().reset();
    } catch (e) {
      console.error("[error] clear login state failed:", e);
      clearClientAuthStorage();
    }
    navigateTo("/login", { replace: true });
  }
});

// Check for 404
const is404 = computed(() => props.error?.statusCode === 404);

const errorTitle = computed(() => {
  if (is404.value) return "页面找不到了";
  return "页面出错了";
});

const errorDescription = computed(() => {
  if (is404.value) return "抱歉，您访问的页面不存在或已被移除。";
  return (
    props.error?.message || "抱歉，服务器暂时无法处理您的请求。请联系管理员！"
  );
});
</script>

<template>
  <div
    class="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4"
  >
    <div
      class="bg-white p-12 rounded-2xl shadow-xl max-w-lg w-full text-center transform transition-all hover:scale-105 duration-300"
    >
      <!-- Icon Section -->
      <div class="mb-8 flex justify-center">
        <div class="relative">
          <div
            class="absolute inset-0 bg-opacity-20 rounded-full blur-xl"
            :class="is404 ? 'bg-orange-400' : 'bg-red-400'"
          />
          <van-icon
            :name="is404 ? 'cross' : 'warning-o'"
            size="5rem"
            :color="is404 ? '#f97316' : '#ef4444'"
            class="relative z-10 block"
          />
        </div>
      </div>

      <!-- Text Section -->
      <h1 class="text-3xl font-bold text-gray-800 mb-4">{{ errorTitle }}</h1>
      <p class="text-gray-500 mb-8 text-lg leading-relaxed">
        {{ errorDescription }}
      </p>

      <!-- Technical Code (Optional) -->
      <div
        v-if="!is404 && error?.statusCode"
        class="mb-6 font-mono text-sm text-gray-400 bg-gray-100 py-2 px-4 rounded inline-block"
      >
        Error Code: {{ error.statusCode }}
      </div>
      <div
        v-if="is404"
        class="mb-6 font-mono text-sm text-gray-400 bg-gray-100 py-2 px-4 rounded inline-block"
      >
        Error Code: 404
      </div>

      <!-- Action Button -->
      <div>
        <van-button type="primary" size="large" @click="handleError">
          <van-icon name="wap-home" class="align-middle mr-1" />
          返回首页
        </van-button>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Optional styling enhancement if needed beyond Tailwind */
</style>
